import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";

const QrDisplayPage = () => {
  const [searchParams] = useSearchParams();
  const session  = searchParams.get("session") || "";
  const api      = searchParams.get("api") || "";
  const phoneUrl = `${window.location.origin}/qr-phone?session=${session}&api=${encodeURIComponent(api)}`;

  // QR 크기를 창 높이에 맞게 동적 계산
  const calcQrSize = () =>
    Math.min(window.innerWidth - 80, window.innerHeight - 280, 480);

  const [qrSize, setQrSize] = useState(calcQrSize);

  useEffect(() => {
    const onResize = () => setQrSize(calcQrSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleClose = () => {
    if (window.opener) {
      try {
        window.opener.postMessage({ type: "qr-display-close", session }, "*");
      } catch (_) {}
    }
    window.close();
  };

  useEffect(() => {
    const onUnload = () => {
      if (window.opener) {
        try {
          window.opener.postMessage({ type: "qr-display-close", session }, "*");
        } catch (_) {}
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [session]);

  return (
    <div style={styles.page}>
      {/* 헤더 */}
      <div style={styles.header}>
        <button
          type="button"
          onClick={handleClose}
          aria-label="닫기"
          style={styles.closeBtn}
        >
          ✕
        </button>
        <span style={{ fontSize: 32 }}>📱</span>
        <h2 style={styles.title}>선탑자 전화번호 QR 입력</h2>
        <p style={styles.subtitle}>QR 코드를 스캔한 후 이름과 전화번호를 입력하세요</p>
      </div>

      {/* QR 코드 영역 — 남은 공간 채움 */}
      <div style={styles.body}>
        <div style={styles.qrWrap}>
          <QRCodeCanvas value={phoneUrl} size={qrSize} level="M" includeMargin />
        </div>
        <p style={styles.desc}>입력 즉시 일괄입력 표에 순차 등록됩니다</p>
      </div>

      {/* 푸터 */}
      <div style={styles.footer}>
        <div style={styles.sessionBox}>
          <span style={styles.sessionLabel}>세션</span>
          <span style={styles.sessionVal}>{session}</span>
        </div>
        <div style={styles.waitingRow}>
          <div style={{ ...styles.dot, animation: "pulse 1.2s infinite" }} />
          <span style={styles.waiting}>입력 대기 중…</span>
        </div>
        <button type="button" onClick={handleClose} style={styles.footerCloseBtn}>
          닫기
        </button>
      </div>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.2} }
        html, body { margin:0; padding:0; height:100%; overflow:hidden; background:#1b5e20; }
        #root { height:100%; }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#1b5e20",
    fontFamily: "'Noto Sans KR','Malgun Gothic',sans-serif",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  header: {
    backgroundColor: "#2e7d32",
    color: "#fff",
    padding: "18px 20px 14px",
    textAlign: "center",
    flexShrink: 0,
    borderBottom: "3px solid #81c784",
    position: "relative",
  },
  closeBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 36,
    height: 36,
    padding: 0,
    border: "none",
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "#fff",
    fontSize: "1.25rem",
    cursor: "pointer",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    margin: "6px 0 0",
    fontSize: "1.25rem",
    fontWeight: "bold",
    letterSpacing: "0.02em",
  },
  subtitle: {
    margin: "6px 0 0",
    fontSize: "0.85rem",
    opacity: 0.85,
  },
  body: {
    flex: "1 1 0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: "16px 20px",
    minHeight: 0,
  },
  qrWrap: {
    border: "5px solid #81c784",
    borderRadius: 12,
    padding: 10,
    backgroundColor: "#fff",
    display: "inline-flex",
    boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
  },
  desc: {
    margin: 0,
    fontSize: "0.9rem",
    color: "#c8e6c9",
    textAlign: "center",
  },
  footer: {
    flexShrink: 0,
    backgroundColor: "#1a3a1a",
    padding: "12px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    borderTop: "2px solid #388e3c",
  },
  sessionBox: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    backgroundColor: "#2e7d32",
    borderRadius: 6,
    padding: "5px 12px",
  },
  sessionLabel: {
    fontSize: "0.72rem",
    color: "#a5d6a7",
    flexShrink: 0,
    fontWeight: "bold",
  },
  sessionVal: {
    fontSize: "0.7rem",
    color: "#e8f5e9",
    wordBreak: "break-all",
  },
  waitingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor: "#69f0ae",
    flexShrink: 0,
  },
  waiting: {
    margin: 0,
    fontSize: "0.88rem",
    color: "#69f0ae",
    fontWeight: "bold",
  },
  footerCloseBtn: {
    marginTop: 4,
    alignSelf: "center",
    minWidth: 110,
    padding: "7px 16px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.4)",
    backgroundColor: "#2e7d32",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "0.92rem",
    cursor: "pointer",
  },
};

export default QrDisplayPage;
